<template>
  <v-card dark color="#1E1E1E" elevation="0" max-width="900px" class="mx-auto mb-15 mt-5">
    <v-row class="d-flex justify-center mb-5">
      <v-col cols="12">
        <v-list dark color="transparent" rounded>
          <v-row v-if="loading" class="d-flex justify-center">
            <v-col cols="12" class="px-5">
              <v-progress-linear indeterminate color="primary"></v-progress-linear>
            </v-col>
          </v-row>

          <v-row v-if="!loading" class="d-flex justify-center">
            <v-alert class="mx-5 mb-0" width="100%" border="left" colored-border color="primary" elevation="2" style="background: #272727">
              <div class="d-flex justify-space-between align-center">
                <span class="text-h6 font-weight-bold white--text"> {{ selectedStudio.name }}</span>
                <v-btn
                  v-show="selectedStudio?.code && selectedStudio.code !== 'dietlikon'"
                  @click="changeStudio"
                  class="ml-auto"
                  text
                  small
                  color="grey lighten-1">
                  Standort wechseln
                </v-btn>
              </div>
            </v-alert>
          </v-row>

          <v-row v-if="!loading && userService.hasRight('c_create')" class="mt-2">
            <v-col cols="12">
              <v-switch class="mx-5 mt-0" dense label="Ausgeblendete Verträge anzeigen" v-model="showHiddenContracts"></v-switch>
            </v-col>
          </v-row>

          <v-row no-gutters v-if="!loading">
            <v-col cols="12" class="px-5 mb-2">
              <div :class="{ 'mt-5': !userService.hasRight('c_create') }" class="text-overline grey--text text--lighten-1">Verfügbare Verträge</div>
            </v-col>
            <v-col cols="12">
              <v-list-item-group class="mx-0 px-0">
                <v-list-item
                  @click="editCreateMode(contract._id)"
                  class="mx-0 px-0 mb-1 rounded"
                  v-for="contract of filteredContracts"
                  :key="contract._id">
                  <v-row no-gutters class="py-2 align-center">
                    <v-col cols="7" class="d-flex align-center px-2">
                      <v-btn v-if="userService.hasRight('c_create')" @click.stop="updatePublishedStatus(contract._id)" icon class="mr-2">
                        <v-icon v-if="contract.published" color="green lighten-1">mdi-eye-check-outline</v-icon>
                        <v-icon v-else color="grey darken-1">mdi-eye-off-outline</v-icon>
                      </v-btn>
                      <div class="text-body-1 font-weight-medium white--text">{{ contract.internalTitle }}</div>
                    </v-col>
                    <v-col cols="5" class="d-flex flex-row justify-end align-center px-2">
                      <v-tooltip left content-class="grey darken-3">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon color="grey lighten-1" v-bind="attrs" v-on="on" small class="mr-1">
                            <v-icon>mdi-information-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>
                          <span class="font-weight-bold">{{ getContractTitle(contract) }}</span>
                          <br />
                          <span class="caption grey--text">Erstellt: {{ $moment(contract.createdAt).format("DD.MM.YY") }}</span>
                          <br />
                          <div class="mt-2" v-for="studioId in contract.enabledStudios" :key="studioId + '-studioIndex' + contract._id">
                            <span class="caption">- {{ getNameOfStudio(studioId) }}</span>
                          </div>
                        </span>
                      </v-tooltip>

                      <v-btn v-if="userService.hasRight('c_create')" @click.stop="copyMode(contract._id)" icon small class="mr-1">
                        <v-icon color="grey lighten-1">mdi-content-copy</v-icon>
                      </v-btn>

                      <v-btn @click.stop="editCreateMode(contract._id)" icon>
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-list-item>
              </v-list-item-group>
            </v-col>
          </v-row>
          <v-divider class="mx-5 mb-4 grey darken-2"></v-divider>
          <div class="px-5 mt-4 mb-2" v-if="!loading && userService.hasRight('c_create_customer')">
            <div class="text-overline grey--text text--lighten-1">Sales & Upgrades</div>

            <v-card class="mb-4" flat>
              <v-list-item @click="$router.push('/dashboard/contract/upgrade/created')" three-line>
                <v-list-item-avatar tile size="40" color="orange darken-4" class="rounded mr-4">
                  <v-icon large color="white">mdi-arrow-up-bold-box-outline</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-h6 orange--text text--lighten-1"> Zusatzpaket Upgrade </v-list-item-title>
                  <v-list-item-subtitle class="grey--text text--lighten-1">
                    Hier kannst du Upgrades für bestehende Kunden erfassen und verwalten.
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="false">
                  <v-btn color="orange" outlined @click="$router.push('/dashboard/contract/upgrade/created')">
                    Zu den Upgrades
                    <v-icon right small>mdi-chevron-right</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-card>
          </div>

          <div class="px-5 mb-6" v-if="!loading && userService.hasRight('c_create_customer')">
            <v-row align="center">
              <v-col cols="12" md="8">
                <v-card class="d-flex align-center pa-3" flat @click="fetchCompanyConfirmations" ripple>
                  <v-avatar color="primary" size="40" class="mr-3">
                    <v-icon small color="white">mdi-download</v-icon>
                  </v-avatar>
                  <div>
                    <div class="font-weight-bold">Firmenrabattliste herunterladen</div>
                    <div class="caption grey--text">Aktuelle Excel-Liste der bestätigten Firmen</div>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" md="4" v-if="userService.hasRight('c_create')" class="d-flex justify-end justify-md-center">
                <v-btn text small color="grey darken-1" @click="showExcelImport = !showExcelImport">
                  <v-icon left small>mdi-upload</v-icon>
                  {{ showExcelImport ? "Abbrechen" : "Neue Liste importieren" }}
                </v-btn>
              </v-col>
            </v-row>

            <v-expand-transition>
              <div v-if="showExcelImport" class="mt-3">
                <div
                  class="input-wrapper dark-dropzone"
                  @dragover.prevent="handleDragOver"
                  @drop.prevent="handleDrop"
                  @click.stop="$refs.fileInput.click()">
                  <div class="text-center">
                    <v-icon color="green lighten-1" large class="mb-2">mdi-file-excel-box</v-icon>
                    <div class="caption grey--text text--lighten-1">
                      <strong>Firmenrabattliste.xlsx</strong> hier reinziehen<br />oder klicken zum Auswählen
                    </div>
                  </div>
                  <input id="jsonFile" type="file" class="input-hidden" ref="fileInput" @change="importExcel" />
                </div>
              </div>
            </v-expand-transition>
          </div>
        </v-list>
      </v-col>
    </v-row>

    <v-divider v-if="!loading && userService.hasRight('g_managePhysiotherapist')" class="my-6 grey darken-2"></v-divider>

    <div class="px-4 pb-6" v-if="!loading && userService.hasRight('g_managePhysiotherapist')">
      <div class="d-flex justify-space-between align-center mb-4">
        <div class="text-overline grey--text text--lighten-1 ml-1">Physiotherapeuten <v-chip color="primary" small>coming soon</v-chip></div>
        <v-btn small color="teal accent-4" outlined @click="openPhysioDialog()"> <v-icon left small>mdi-plus</v-icon> Neu </v-btn>
      </div>

      <v-row>
        <v-col cols="12" v-if="physiotherapists.length === 0">
          <div class="text-center grey--text text--darken-1 py-5 caption font-italic">Keine Physiotherapeuten für diesen Standort hinterlegt.</div>
        </v-col>

        <v-col v-for="physio in physiotherapists" :key="physio._id" cols="12" sm="6" md="4">
          <v-card color="grey darken-3" dark outlined class="rounded-lg">
            <v-list-item>
              <v-list-item-avatar color="teal darken-4">
                <v-icon color="teal lighten-2">mdi-doctor</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold white--text">{{ physio.firstName }} {{ physio.lastName }}</v-list-item-title>
                <v-list-item-subtitle class="caption grey--text">{{ physio.city }}</v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action v-if="userService.hasRight('g_managePhysiotherapist')">
                <v-menu bottom left dark offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon color="grey">mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense color="grey darken-3">
                    <v-list-item @click="openPhysioDialog(physio)">
                      <v-list-item-icon><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
                      <v-list-item-title>Bearbeiten</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="deletePhysio(physio)">
                      <v-list-item-icon><v-icon small color="red lighten-1">mdi-delete</v-icon></v-list-item-icon>
                      <v-list-item-title class="red--text text--lighten-1">Löschen</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
            </v-list-item>
            <v-divider class="grey darken-2"></v-divider>
            <v-card-text class="caption grey--text text--lighten-1 pt-2">
              <div v-if="physio.phone" class="mb-1 d-flex align-center">
                <v-icon x-small class="mr-2" color="grey">mdi-phone</v-icon> {{ physio.phone }}
              </div>
              <div v-if="physio.email" class="d-flex align-center">
                <v-icon x-small class="mr-2" color="grey">mdi-email</v-icon> {{ physio.email }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-dialog v-model="physioDialog" max-width="500px" dark>
      <v-card color="grey darken-4">
        <v-card-title class="grey darken-3 pb-2 pt-3">
          <span class="text-h6 white--text">{{ editedPhysio._id ? "Physio bearbeiten" : "Neuer Physio" }}</span>
        </v-card-title>
        <v-card-text class="pt-5">
          <v-container class="pa-0">
            <v-row dense>
              <v-col cols="6">
                <v-text-field v-model="editedPhysio.firstName" label="Vorname" outlined dense color="primary"></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="editedPhysio.lastName" label="Nachname" outlined dense color="primary"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="editedPhysio.address" label="Adresse" outlined dense color="primary"></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-text-field v-model="editedPhysio.zip" label="PLZ" outlined dense color="primary"></v-text-field>
              </v-col>
              <v-col cols="8">
                <v-text-field v-model="editedPhysio.city" label="Ort" outlined dense color="primary"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="editedPhysio.email" label="Email" outlined dense prepend-inner-icon="mdi-email" color="primary"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedPhysio.phone"
                  label="Telefon"
                  outlined
                  dense
                  prepend-inner-icon="mdi-phone"
                  color="primary"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="editedPhysio.description" label="Notizen / Beschreibung" outlined dense rows="3" color="primary"></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="grey darken-3">
          <v-spacer></v-spacer>
          <v-btn color="grey lighten-1" text @click="closePhysioDialog">Abbrechen</v-btn>
          <v-btn color="primary" text @click="savePhysio">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "../../../api"
import contractSettings from "./contractSettings.vue"
import Payment from "../payment/Payment.vue"
import userService from "../../../services/userService"
import { mapState } from "vuex"

export default {
  components: { contractSettings, Payment },
  data() {
    return {
      showExcelImport: false,
      searchTimeout: null,
      companyConfirmationsDialog: false,
      showHiddenContracts: false,
      userService,
      loading: true,
      contracts: [],
      studios: [],
      userStudio: null,
      studioSelect: null,

      // Physio Logic
      physiotherapists: [],
      physioDialog: false,
      editedPhysio: {
        firstName: "",
        lastName: "",
        address: "",
        zip: "",
        city: "",
        email: "",
        phone: "",
        description: "",
        studios: [],
      },
      defaultPhysio: {
        firstName: "",
        lastName: "",
        address: "",
        zip: "",
        city: "",
        email: "",
        phone: "",
        description: "",
        studios: [],
      },
    }
  },
  async created() {
    let studios = await api.fetchStudios()
    this.studios = studios

    let userStudio = await api.getUserStudio()
    this.userStudio = userStudio.studio._id.toString()
    this.studioSelect = userStudio.studio._id

    // Set current studio for new physios
    this.defaultPhysio.studios = [this.studioSelect]

    await Promise.all([this.fetchContracts(), this.fetchPhysios()])

    this.loading = false
  },
  computed: {
    ...mapState("studioStore", ["selectedStudio"]),
    // NEU: Die Filter-Logik hierhin verschieben
    filteredContracts() {
      if (!this.contracts) return []

      return this.contracts.filter((contract) => {
        // 1. Studio Filter (Deine Original-Logik)
        // Prüft, ob der Vertrag für das ausgewählte Studio freigeschaltet ist
        const matchesStudio = contract.enabledStudios.some((e) => this.studioSelect && this.studioSelect.includes(e))

        // 2. Sichtbarkeit Filter (Switch Logik)
        // Zeigen, wenn veröffentlicht ODER (User hat Recht UND Switch ist an)
        const isVisible = contract.published || (this.userService.hasRight("c_create") && this.showHiddenContracts)

        return matchesStudio && isVisible
      })
    },
  },
  methods: {
    // --- PHYSIO METHODS ---
    async fetchPhysios() {
      try {
        this.physiotherapists = await api.fetchPhysiotherapists()
      } catch (e) {
        console.error("Error fetching physios", e)
      }
    },
    openPhysioDialog(item) {
      if (item) {
        this.editedPhysio = Object.assign({}, item)
      } else {
        this.editedPhysio = Object.assign({}, this.defaultPhysio)
        // Ensure the current studio is set
        if (!this.editedPhysio.studios.includes(this.studioSelect)) {
          this.editedPhysio.studios.push(this.studioSelect)
        }
      }
      this.physioDialog = true
    },
    closePhysioDialog() {
      this.physioDialog = false
      this.$nextTick(() => {
        this.editedPhysio = Object.assign({}, this.defaultPhysio)
      })
    },
    async savePhysio() {
      if (!this.editedPhysio.firstName || !this.editedPhysio.lastName) {
        return this.$toast.error("Vorname und Nachname sind Pflichtfelder")
      }

      try {
        if (this.editedPhysio._id) {
          // Update
          await api.updatePhysiotherapist(this.editedPhysio._id, this.editedPhysio)
          this.$toast.success("Physiotherapeut aktualisiert")
        } else {
          // Create
          await api.createPhysiotherapist(this.editedPhysio)
          this.$toast.success("Physiotherapeut erstellt")
        }
        await this.fetchPhysios()
        this.closePhysioDialog()
      } catch (e) {
        this.$toast.error("Fehler beim Speichern")
        console.error(e)
      }
    },
    async deletePhysio(item) {
      if (confirm(`Möchtest du ${item.firstName} ${item.lastName} wirklich löschen?`)) {
        try {
          await api.deletePhysiotherapist(item._id)
          this.$toast.success("Gelöscht")
          this.fetchPhysios()
        } catch (e) {
          this.$toast.error("Fehler beim Löschen")
        }
      }
    },

    // --- EXISTING METHODS ---
    async importExcel(event) {
      const file = event.target.files ? event.target.files[0] : event.dataTransfer.files[0]
      if (!file) return

      if (file.name.toString() !== "Firmenrabattliste.xlsx") return alert("Dateiname muss 'Firmenrabattliste.xlsx' sein")

      try {
        await api.uploadCompanyDiscountList(file, file.name)
        this.$toast.success("Datei erfolgreich hochgeladen")
        this.showExcelImport = false
      } catch (error) {
        this.$toast.error("Datei konnte nicht hochgeladen werden")
      }
    },
    handleDragOver(event) {},
    handleDrop(event) {
      const files = event.dataTransfer.files
      if (files.length > 0) {
        this.importExcel({ target: { files: files }, dataTransfer: { files: files } })
      }
    },
    async fetchCompanyConfirmations() {
      try {
        await api.fetchCompanyConfirmations()
      } catch (error) {
        console.error(error)
      }
    },
    async updatePublishedStatus(contractId) {
      let contract = this.contracts.find((c) => c._id === contractId)
      if (!contract) return this.$toast.error("Vertrag nicht gefunden")
      contract.published = !contract.published
      await api.changeContractPublishedStatus({ contract }).catch((e) => this.$toast.error("Fehler beim Aktualisieren des Vertrags"))
      this.$toast.success("Vertrag erfolgreich aktualisiert")
    },
    getNameOfStudio(studioId) {
      let studio = this.studios.find((s) => s._id === studioId)
      return studio ? studio.name : "Unbekannt"
    },
    copyMode(id) {
      this.$router.push("/dashboard/contract/settings/" + id)
    },
    getContractTitle(contract) {
      let title = contract.internalTitle ? contract.internalTitle : contract.formFields.find((e) => e.inputType === "title").inputValue
      return title
    },
    async fetchContracts() {
      let contracts = await api.fetchContracts()
      this.contracts = contracts
    },
    changeStudio() {
      window.app.$root.$children[0].showStudioSelection = true
    },
    editCreateMode(id) {
      if (this.studioSelect.toString() === this.userStudio.toString()) {
        this.$router.push("/dashboard/contract/create/" + id)
      } else {
        this.$toast.error("Bitte wechsel in das richtige Studio, um den Vertrag zu erstellen")
        window.app.$root.$children[0].showStudioSelection = true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.input-wrapper {
  border: 2px dashed #616161; // Darker border
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  min-height: 100px;
  transition: all 0.3s ease;

  &:hover {
    background-color: #333 !important;
    border-color: #4caf50 !important;
  }
}

.dark-dropzone {
  background-color: #1e1e1e; // Dark background
}

.input-hidden {
  display: none;
}
</style>
